import {
  ArrowLeftRight,
  ArrowRight,
  Bell,
  BookOpen,
  Calculator,
  Car,
  ChartLine,
  ChevronDown,
  ClipboardCheck,
  FileCheck,
  FileSpreadsheet,
  FileText,
  Menu,
  ReceiptText,
  Route,
  X,
} from "lucide-react"
import type { ReactNode, RefObject } from "react"
import { useEffect, useId, useRef } from "react"

import type { NavEntry, NavGroup, NavIcon, NavLink, NavTone } from "@/brand/nav-model"
import { isNavGroup } from "@/brand/nav-model"
import { Logo } from "@/core/components/logo"
import { useLocale, useLocalHref, useUi } from "@/core/i18n/locale-context"
import { gateCta, isComingSoon } from "@/core/launch"
import { getSiteName } from "@/core/site"
import { Button } from "@/ui/button"

interface SiteNavProps {
  items: NavEntry[]
  signInHref: string
  ctaHref: string
  ctaLabel: string
  siteName?: string
  /** Language switcher, shown beside the sign-in link and at the end of the mobile menu. */
  languageSwitcher?: ReactNode
  /**
   * Whether the visitor is signed in. "pending" (session not known yet, e.g. the prerendered HTML)
   * hides Sign in and the CTA (`.auth-pending`, shown again by CSS after 3 s) so neither flashes and swaps; "in" drops Sign in
   * and shows `accountMenu` after the CTA.
   */
  authState?: "pending" | "in" | "out"
  accountMenu?: ReactNode
}

const focusRing = "outline-none focus-visible:ring-2 focus-visible:ring-ring"
const topLinkClass = `inline-flex min-h-11 items-center gap-1 rounded-md px-2 font-medium text-muted-foreground hover:text-foreground ${focusRing}`
const menuLinkClass = `flex min-h-11 w-full items-center rounded-md px-2 font-medium text-foreground hover:bg-muted ${focusRing}`
/** Hides the native disclosure triangle; the chevron icon shows the state instead. */
const summaryClass = "cursor-pointer list-none [&::-webkit-details-marker]:hidden"

const ICONS: Record<NavIcon, typeof Route> = {
  route: Route,
  "receipt-text": ReceiptText,
  "file-text": FileText,
  "file-check": FileCheck,
  "file-spreadsheet": FileSpreadsheet,
  calculator: Calculator,
  "arrow-left-right": ArrowLeftRight,
  "clipboard-check": ClipboardCheck,
  "chart-line": ChartLine,
  car: Car,
  "book-open": BookOpen,
  bell: Bell,
}

/** Soft-tinted chip tones (design-system rule 4). */
const TONES: Record<NavTone, string> = {
  primary: "bg-primary/15 text-primary",
  success: "bg-success-soft text-success",
  warning: "bg-warning-soft text-warning",
  violet: "bg-accent text-accent-foreground",
  muted: "bg-muted text-muted-foreground",
}

const NavChip = ({
  icon,
  tone = "primary",
  size,
}: Pick<NavLink, "icon" | "tone"> & { size: string }) => {
  const Icon = icon ? ICONS[icon] : null
  if (!Icon) return null
  return (
    <span
      data-nav-chip
      className={`${size} ${TONES[tone]} grid shrink-0 place-items-center rounded-lg transition-transform duration-150 group-hover/item:scale-105 motion-reduce:transition-none`}
    >
      <Icon className="size-[18px]" aria-hidden="true" />
    </span>
  )
}

/**
 * Hover and focus highlight: a pseudo-element that fades and scales in (opacity and transform
 * only, so it never repaints or reflows the list).
 */
const itemHighlight =
  "group/item relative isolate before:absolute before:inset-0 before:-z-10 before:scale-[0.97] before:rounded-lg before:bg-muted before:opacity-0 before:transition-[opacity,transform] before:duration-150 hover:before:scale-100 hover:before:opacity-100 focus-visible:before:scale-100 focus-visible:before:opacity-100 motion-reduce:before:transition-none"

const DropdownLink = ({ label, href, description, icon, tone }: NavLink) => (
  <a
    href={href}
    className={`${itemHighlight} flex min-h-11 items-center gap-3 rounded-lg px-3 py-2 ${focusRing}`}
  >
    <NavChip icon={icon} tone={tone} size="size-9" />
    <span className="grid">
      <span className="text-foreground font-medium">{label}</span>
      {description && <span className="text-muted-foreground text-sm">{description}</span>}
    </span>
  </a>
)

/**
 * Desktop dropdown: a native `<details>` disclosure (works before hydration, no menu role; links
 * are plain links in the tab order). `name` makes the groups exclusive: opening one closes the
 * other. The panel is absolutely positioned, so opening it never moves the page. Motion lives in
 * styles/index.css (`.nav-dd`, `.nav-panel`, `.nav-stagger`): transform and opacity only.
 */
const Dropdown = ({ label, items }: NavGroup) => {
  const rows = items.filter((l) => !l.footer)
  const footer = items.filter((l) => l.footer)
  return (
    <details name="site-nav" className="nav-dd group relative">
      <summary className={`${topLinkClass} ${summaryClass}`}>
        {label}
        <ChevronDown
          className="size-4 transition-transform duration-150 group-open:rotate-180 motion-reduce:transition-none"
          aria-hidden="true"
        />
      </summary>
      <div className="nav-panel border-border bg-popover text-popover-foreground absolute top-full left-0 z-20 mt-1 w-96 rounded-xl border p-2 shadow-md">
        <ul className="grid gap-0.5">
          {rows.map((item) => (
            <li key={item.href} className="nav-stagger">
              <DropdownLink {...item} />
            </li>
          ))}
        </ul>
        {footer.map((item) => (
          <a
            key={item.href}
            href={item.href}
            className={`nav-stagger text-muted-foreground hover:text-foreground border-border mt-2 flex min-h-11 items-center justify-between rounded-lg border-t px-3 pt-2 text-sm font-medium ${focusRing}`}
          >
            {item.label}
            <ArrowRight className="size-4" aria-hidden="true" />
          </a>
        ))}
      </div>
    </details>
  )
}

/** Closes open disclosures on Escape (focus back on the summary), outside click and focus leaving. */
function useDisclosureDismiss(root: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const nav = root.current
    if (!nav) return
    const openOnes = () => [...nav.querySelectorAll<HTMLDetailsElement>("details[open]")]
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return
      const innermost = openOnes().at(-1)
      if (!innermost) return
      innermost.open = false
      innermost.querySelector("summary")?.focus()
    }
    const onPointer = (e: PointerEvent) => {
      for (const d of openOnes()) if (!d.contains(e.target as Node)) d.open = false
    }
    const onFocusOut = (e: FocusEvent) => {
      const next = e.relatedTarget as Node | null
      if (!next) return
      for (const d of openOnes()) if (!d.contains(next)) d.open = false
    }
    document.addEventListener("keydown", onKey)
    document.addEventListener("pointerdown", onPointer)
    nav.addEventListener("focusout", onFocusOut)
    return () => {
      document.removeEventListener("keydown", onKey)
      document.removeEventListener("pointerdown", onPointer)
      nav.removeEventListener("focusout", onFocusOut)
    }
  }, [root])
}

export const SiteNav = ({
  items: rawItems,
  signInHref,
  ctaHref: rawCtaHref,
  ctaLabel: rawCtaLabel,
  siteName = getSiteName(),
  languageSwitcher,
  authState = "out",
  accountMenu,
}: SiteNavProps) => {
  const ui = useUi()
  const zh = useLocale() === "zh"
  const localHref = useLocalHref()
  const { href: ctaHref, label: ctaLabel } = gateCta(
    { href: rawCtaHref, label: rawCtaLabel },
    useLocale(),
  )
  const showSignIn = !isComingSoon() && authState !== "in"
  const hideUntilKnown = authState === "pending" ? "auth-pending" : ""
  const items = rawItems.map((entry) =>
    isNavGroup(entry)
      ? { ...entry, items: entry.items.map((l) => ({ ...l, href: localHref(l.href) })) }
      : { ...entry, href: localHref(entry.href) },
  )
  const ref = useRef<HTMLElement>(null)
  const idPrefix = useId()
  useDisclosureDismiss(ref)
  return (
    <nav
      ref={ref}
      aria-label={ui.mainNav}
      className="border-border bg-card px-band relative flex min-h-14 items-center gap-3 border-b md:min-h-16 md:gap-6"
    >
      <a
        href={localHref("/")}
        className={`flex min-h-11 items-center gap-2.5 rounded-md text-base font-bold ${focusRing}`}
      >
        <Logo className="size-[30px] shrink-0" />
        {siteName}
      </a>

      <ul className="hidden items-center gap-1 lg:flex">
        {items.map((entry) => (
          <li key={entry.label}>
            {isNavGroup(entry) ? (
              <Dropdown {...entry} />
            ) : (
              <a href={entry.href} className={topLinkClass}>
                {entry.label}
              </a>
            )}
          </li>
        ))}
      </ul>

      <span className="flex-1" />
      {languageSwitcher && <div className="max-lg:hidden">{languageSwitcher}</div>}
      {showSignIn && (
        <a href={signInHref} className={`${topLinkClass} max-lg:hidden ${hideUntilKnown}`}>
          {ui.signIn}
        </a>
      )}
      <Button asChild className={`h-11 lg:h-9 ${hideUntilKnown}`}>
        <a href={ctaHref}>{ctaLabel}</a>
      </Button>
      {authState === "in" && accountMenu}

      {/* Below 1024 px: one disclosure holding every group as a labelled list. */}
      <details className="group/menu lg:hidden">
        <summary
          aria-label={ui.menu}
          className={`text-foreground hover:bg-muted grid size-11 place-items-center rounded-md ${summaryClass} ${focusRing}`}
        >
          <Menu className="size-5 group-open/menu:hidden" aria-hidden="true" />
          <X className="hidden size-5 group-open/menu:block" aria-hidden="true" />
        </summary>
        <div className="nav-sheet border-border bg-card absolute inset-x-0 top-full z-20 grid max-h-[calc(100dvh-3.5rem)] gap-5 overflow-y-auto border-b p-4 shadow-md">
          {items.map((entry, i) =>
            isNavGroup(entry) ? (
              <div key={entry.label} className="nav-stagger">
                <p
                  id={`${idPrefix}-group-${i}`}
                  className={`text-muted-foreground mb-1 text-xs font-semibold ${zh ? "" : "tracking-wide uppercase"}`}
                >
                  {entry.label}
                </p>
                <ul aria-labelledby={`${idPrefix}-group-${i}`}>
                  {entry.items.map(({ label, href, icon, tone, footer }) => (
                    <li key={href}>
                      <a
                        href={href}
                        className={`${menuLinkClass} gap-3 ${footer ? "text-muted-foreground justify-between" : ""}`}
                      >
                        <NavChip icon={icon} tone={tone} size="size-8" />
                        {label}
                        {footer && <ArrowRight className="size-4" aria-hidden="true" />}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null,
          )}
          <ul className="border-border border-t pt-3">
            {items
              .filter((e): e is NavLink => !isNavGroup(e))
              .map(({ label, href }) => (
                <li key={href}>
                  <a href={href} className={menuLinkClass}>
                    {label}
                  </a>
                </li>
              ))}
            {showSignIn && (
              <li className={hideUntilKnown}>
                <a href={signInHref} className={menuLinkClass}>
                  {ui.signIn}
                </a>
              </li>
            )}
            {languageSwitcher && (
              <li className="[&_a]:text-foreground [&_a]:hover:bg-muted [&_a]:w-full">
                {languageSwitcher}
              </li>
            )}
          </ul>
        </div>
      </details>
    </nav>
  )
}
